/**
 * Custom Tools page — list, view, test, and deploy AI-generated StructuredTools.
 *
 * Each tool starts as a 'draft' created by the Build Agent.
 * Users review the source code, test it with a sample input, then deploy.
 */

import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { usePractice } from "../context/PracticeContext";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { customToolsApi, type CustomTool } from "../api/customTools";
import { PageSkeleton } from "../components/PageSkeleton";
import { EmptyState } from "../components/EmptyState";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Wrench, Play, Rocket, EyeOff, ChevronDown, ChevronUp, CheckCircle, XCircle } from "lucide-react";

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  active: "Active",
  disabled: "Disabled",
};

const STATUS_COLORS: Record<string, "default" | "success" | "neutral"> = {
  draft: "default",
  active: "success",
  disabled: "neutral",
};

function ToolCard({
  tool,
  companyId,
  onRefresh,
}: {
  tool: CustomTool;
  companyId: string;
  onRefresh: () => void;
}) {
  const [codeOpen, setCodeOpen] = useState(false);
  const [testInput, setTestInput] = useState("{}");
  const [testResult, setTestResult] = useState<{ success: boolean; output?: string; error?: string; durationMs: number } | null>(null);
  const [testRunning, setTestRunning] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const qc = useQueryClient();

  const deployMutation = useMutation({
    mutationFn: () => customToolsApi.deploy(companyId, tool.id),
    onSuccess: () => { onRefresh(); setActionError(null); },
    onError: (e: Error) => setActionError(e.message),
  });

  const disableMutation = useMutation({
    mutationFn: () => customToolsApi.disable(companyId, tool.id),
    onSuccess: () => { onRefresh(); setActionError(null); },
    onError: (e: Error) => setActionError(e.message),
  });

  async function runTest() {
    setTestRunning(true);
    setTestResult(null);
    try {
      const input = JSON.parse(testInput);
      const result = await customToolsApi.test(companyId, tool.id, input);
      setTestResult(result);
    } catch (e) {
      setTestResult({ success: false, error: String(e), durationMs: 0 });
    } finally {
      setTestRunning(false);
    }
  }

  return (
    <Card>
      <CardContent className="p-0">
        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-muted">
              <Wrench className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">{tool.name}</span>
                <StatusBadge
                  status={STATUS_COLORS[tool.status] ?? "default"}
                  label={STATUS_LABELS[tool.status] ?? tool.status}
                />
              </div>
              {tool.description && (
                <p className="text-xs text-muted-foreground mt-0.5">{tool.description}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {tool.status === "draft" && (
              <Button
                size="sm"
                variant="default"
                onClick={() => deployMutation.mutate()}
                disabled={deployMutation.isPending}
                className="h-7 text-xs"
              >
                <Rocket className="h-3 w-3 mr-1" />
                Deploy
              </Button>
            )}
            {tool.status === "active" && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => disableMutation.mutate()}
                disabled={disableMutation.isPending}
                className="h-7 text-xs"
              >
                <EyeOff className="h-3 w-3 mr-1" />
                Disable
              </Button>
            )}
            {tool.status === "disabled" && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => deployMutation.mutate()}
                disabled={deployMutation.isPending}
                className="h-7 text-xs"
              >
                <Rocket className="h-3 w-3 mr-1" />
                Re-enable
              </Button>
            )}
          </div>
        </div>

        {actionError && (
          <div className="mx-4 mt-3 text-xs text-destructive">{actionError}</div>
        )}

        {/* Source code toggle */}
        <button
          className="w-full flex items-center justify-between px-4 py-2 text-xs text-muted-foreground hover:bg-muted/30 transition-colors"
          onClick={() => setCodeOpen((v) => !v)}
        >
          <span>Source code</span>
          {codeOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>

        {codeOpen && (
          <div className="px-4 pb-3">
            <pre className="overflow-x-auto rounded-md border border-border bg-muted/30 p-3 text-xs font-mono leading-relaxed whitespace-pre">
              {tool.sourceCode}
            </pre>
          </div>
        )}

        {/* Test panel */}
        <div className="border-t border-border px-4 py-3">
          <p className="text-xs font-medium mb-2">Test</p>
          <Textarea
            className="font-mono text-xs min-h-[60px] resize-none"
            placeholder='{"action": "get", "id": "123"}'
            value={testInput}
            onChange={(e) => setTestInput(e.target.value)}
          />
          <div className="mt-2 flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={runTest}
              disabled={testRunning}
              className="h-7 text-xs"
            >
              <Play className="h-3 w-3 mr-1" />
              {testRunning ? "Running..." : "Run"}
            </Button>
            {testResult && (
              <span className="flex items-center gap-1 text-xs">
                {testResult.success ? (
                  <CheckCircle className="h-3 w-3 text-green-500" />
                ) : (
                  <XCircle className="h-3 w-3 text-destructive" />
                )}
                <span className={testResult.success ? "text-green-600" : "text-destructive"}>
                  {testResult.success ? `OK (${testResult.durationMs}ms)` : "Failed"}
                </span>
              </span>
            )}
          </div>
          {testResult && (
            <pre className="mt-2 overflow-x-auto rounded-md border border-border bg-muted/30 p-2 text-xs font-mono whitespace-pre-wrap">
              {testResult.output ?? testResult.error ?? "No output"}
            </pre>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function CustomTools() {
  const { selectedPractice } = usePractice();
  const companyId = selectedPractice?.id ?? "";
  const { setBreadcrumbs } = useBreadcrumbs();
  useEffect(() => {
    setBreadcrumbs([{ label: "Custom Tools" }]);
  }, [setBreadcrumbs]);

  const { data: tools, isLoading, refetch } = useQuery({
    queryKey: ["custom-tools", companyId],
    queryFn: () => customToolsApi.list(companyId),
    enabled: !!companyId,
  });

  if (isLoading) return <PageSkeleton />;

  return (
    <div className="mx-auto max-w-4xl px-6 py-8">
      <div className="mb-6">
        <h1 className="text-xl font-semibold">Custom Tools</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          AI-generated integrations created by the Build Agent. Review source code, test with
          sample inputs, then deploy to make them available to agent nodes.
        </p>
      </div>

      {!tools?.length ? (
        <EmptyState
          icon={Wrench}
          message="No custom tools yet. Ask the Build Agent to create one — try: “Create a tool that checks pharmacy inventory at our API”."
        />
      ) : (
        <div className="flex flex-col gap-4">
          {(tools ?? []).map((tool) => (
            <ToolCard
              key={tool.id}
              tool={tool}
              companyId={companyId}
              onRefresh={refetch}
            />
          ))}
        </div>
      )}
    </div>
  );
}
